import * as Popover from "@radix-ui/react-popover";
import { LogOut, Settings, UserRound } from "lucide-react";
import { useState } from "react";
import { NavLink } from "react-router";
import { cn } from "../../lib/cn";
import { iniciales, textoDeRoles } from "./usuario";

/**
 * El pie del sidebar con la persona conectada — porteado de `imprenta`.
 *
 * Lo que había acá era otra cosa: el **email** como link y un botón «Salir»
 * suelto. Allá el pie es **quién sos**: avatar con iniciales, nombre y rol, y un
 * engranaje que insinúa que se puede abrir. Al presionarlo sale un menú con el
 * email («Conectado como»), el perfil y cerrar sesión.
 *
 * La diferencia no es de adorno. El email no dice el rol, y el rol es lo que
 * explica por qué esta persona ve unas pantallas y no otras — la pregunta que
 * más se hace en un sistema con permisos.
 *
 * **Colapsado queda solo el avatar**, y el menú sigue estando: es la única
 * forma de llegar al perfil y a salir cuando el texto no está.
 */
export function BloqueDeUsuario({
  nombre,
  email,
  roles,
  colapsado,
  alSalir,
}: {
  nombre: string;
  email: string;
  roles: readonly string[];
  colapsado: boolean;
  alSalir: () => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const cerrar = () => setAbierto(false);

  return (
    <Popover.Root open={abierto} onOpenChange={setAbierto}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={`Cuenta de ${nombre}`}
          className={cn(
            "group flex w-full items-center rounded-md outline-none transition-colors hover:bg-white/5",
            colapsado ? "justify-center p-2" : "gap-3 px-2 py-2",
          )}
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/10 font-bold text-sb-text text-sm transition-colors group-hover:border-white/25">
            {iniciales(nombre)}
          </span>

          {/* Se oculta con ancho y opacidad, no desmontando: la transición del
              colapso queda continua y el texto no salta. */}
          <span
            className={cn(
              "flex min-w-0 flex-col overflow-hidden text-left transition-all duration-300",
              // `flex-1` SOLO expandido: colapsado el span sigue creciendo con
              // `flex: 1 1 0%` aunque mida `w-0`, y empuja el avatar fuera del
              // centro. Se veía como 8 px de desvío que nadie sabía de dónde
              // salían.
              colapsado ? "w-0 opacity-0" : "w-auto flex-1 opacity-100",
            )}
          >
            <span className="truncate font-medium text-sb-text text-sm transition-colors group-hover:text-white">
              {nombre || "Usuario"}
            </span>
            <span className="truncate text-sb-muted text-xs">{textoDeRoles(roles)}</span>
          </span>

          {/* Colapsado el engranaje NO se renderiza, en vez de esconderse con
              `w-0`: `w-0` y `w-4` son la misma propiedad y gana la que esté
              después en el CSS, no en el string — el ícono seguía midiendo
              16px y corría el avatar 8px fuera del centro. */}
          {colapsado ? null : (
            <Settings className="h-4 w-4 shrink-0 text-sb-muted transition-colors group-hover:text-sb-text-2" />
          )}
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="top"
          align="start"
          sideOffset={8}
          className="z-50 w-64 rounded-md border border-neutral-200 bg-white p-2 shadow-lg"
        >
          {/* El email vive ACÁ y no en el pie: es el dato que se consulta de vez
              en cuando —«¿con qué cuenta entré?»— y ocupaba una línea fija. */}
          <div className="mb-1 rounded-md bg-neutral-50 px-2 py-1.5">
            <p className="mb-0.5 font-medium text-neutral-500 text-xs">Conectado como</p>
            <p className="truncate font-semibold text-neutral-900 text-sm" title={email}>
              {email}
            </p>
          </div>

          <div className="my-1 h-px bg-neutral-200" />

          <NavLink
            to="/perfil"
            onClick={cerrar}
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-neutral-800 text-sm hover:bg-neutral-100"
          >
            <UserRound className="h-4 w-4" />
            Mi Perfil
          </NavLink>

          <div className="my-1 h-px bg-neutral-200" />

          <button
            type="button"
            onClick={() => {
              cerrar();
              alSalir();
            }}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-red-600 text-sm hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" />
            Cerrar Sesión
          </button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
